import type { BubbleNode } from './focusFilter';

/**
 * Environment classifier for Compass Explorer bubbles. The scanner
 * doesn't export an `Environment` tag directly on VPCs yet, so we
 * infer from the VPC name/label. Heuristics are deliberately loose —
 * better to surface a group header even if it's approximate than to
 * dump everything into "other."
 *
 * Order matters: prod catches first because "production-stage-db"
 * should still count as prod; then stage, dev, test.
 */

export type EnvKey = 'prod' | 'stage' | 'dev' | 'test' | 'other';

export interface EnvGroup {
  key: EnvKey;
  label: string;
  accent: string;
  bubbles: BubbleNode[];
}

const ENV_ACCENTS: Record<EnvKey, string> = {
  prod: '#dc2626',   // red — "careful, this is live"
  stage: '#f59e0b',  // amber — "preview"
  dev: '#0ea5e9',    // sky — "playground"
  test: '#6366f1',   // indigo — "automation"
  other: '#64748b',  // slate — "unclassified"
};

const ENV_LABELS: Record<EnvKey, string> = {
  prod: 'Production',
  stage: 'Staging',
  dev: 'Development',
  test: 'Test',
  other: 'Other',
};

const ENV_ORDER: readonly EnvKey[] = ['prod', 'stage', 'dev', 'test', 'other'];

export function classifyEnv(name: string): EnvKey {
  const n = name.toLowerCase();
  if (/\b(prod|production)\b/.test(n) || n.startsWith('prod')) return 'prod';
  if (/\b(stage|staging|stg)\b/.test(n) || /stage\d*/.test(n)) return 'stage';
  if (/\b(dev|development)\b/.test(n) || n.includes('_dev')) return 'dev';
  if (/\b(test|qa|uat)\b/.test(n)) return 'test';
  return 'other';
}

export function groupBubblesByEnv(bubbles: readonly BubbleNode[]): EnvGroup[] {
  const byKey = new Map<EnvKey, BubbleNode[]>();
  for (const env of ENV_ORDER) byKey.set(env, []);
  for (const b of bubbles) {
    byKey.get(classifyEnv(b.label))!.push(b);
  }
  const groups: EnvGroup[] = [];
  for (const key of ENV_ORDER) {
    const list = byKey.get(key)!;
    if (list.length === 0) continue;
    groups.push({
      key,
      label: ENV_LABELS[key],
      accent: ENV_ACCENTS[key],
      bubbles: list,
    });
  }
  return groups;
}
